export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:80/api';
export const SITE_SESSION_STORAGE_KEY = 'opticdev.siteSession';
export const AUTH_STORAGE_KEY = 'opticdev.authSession';

function getSiteSessionHeaders() {
  try {
    const session = JSON.parse(localStorage.getItem(SITE_SESSION_STORAGE_KEY) || 'null');
    if (!session) return {};
    return {
      'X-Access-Mode': session.mode,
      ...(session.site?._id ? { 'X-Site-Id': session.site._id } : {}),
    };
  } catch (_error) {
    return {};
  }
}

function getAuthHeaders() {
  try {
    const auth = JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY) || 'null');
    return auth?.token ? { Authorization: `Bearer ${auth.token}` } : {};
  } catch (_error) {
    return {};
  }
}

export async function requestJson(path, options = {}) {
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const response = await fetch(`${API_BASE_URL}${path}`, {
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...getAuthHeaders(),
      ...getSiteSessionHeaders(),
      ...(options.headers || {}),
    },
    ...options,
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(payload.errors?.join(' ') || payload.message || 'Requête impossible.');
    error.status = response.status;
    error.payload = payload;
    error.details = payload.details;
    throw error;
  }

  return payload;
}

export async function requestBlob(path, options = {}) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    headers: {
      ...getAuthHeaders(),
      ...getSiteSessionHeaders(),
      ...(options.headers || {}),
    },
    ...options,
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.errors?.join(' ') || payload.message || 'Téléchargement impossible.');
  }

  return {
    blob: await response.blob(),
    contentType: response.headers.get('Content-Type') || '',
    disposition: response.headers.get('Content-Disposition') || '',
  };
}

export function getCollection(payload) {
  return Array.isArray(payload) ? payload : payload.data || [];
}

export function stripEmptyValues(value) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value.toISOString();
  }

  if (Array.isArray(value)) {
    return value.map(stripEmptyValues).filter((item) => item !== undefined);
  }

  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, item]) => {
      const cleaned = stripEmptyValues(item);
      if (cleaned !== undefined) acc[key] = cleaned;
      return acc;
    }, {});
  }

  if (value === '') return undefined;
  return value;
}

export function getId(value) {
  if (!value) return '';
  return typeof value === 'string' ? value : value._id || '';
}

export function getLabel(value, fallback = '—') {
  if (!value) return fallback;
  if (typeof value === 'string') return value;
  return value.name || value.code || value.fullName || value.email || value._id || fallback;
}

export function getSiteLabel(site) {
  if (!site) return '';
  if (typeof site === 'string') return site;
  return [site.name].filter(Boolean).join(' - ') || site._id || '';
}

export function getStockItemLabel(item) {
  if (!item) return '';
  if (typeof item === 'string') return item;
  return [item.barcode, item.supplier, item.label].filter(Boolean).join(' - ') || item._id || '';
}

export function getCustomerLabel(customer) {
  if (!customer) return '';
  if (typeof customer === 'string') return customer;
  const fullName = [customer.firstName, customer.lastName].filter(Boolean).join(' ');
  return fullName || customer.email || customer.phone || customer._id || '';
}

export function getUserLabel(user) {
  if (!user) return '';
  if (typeof user === 'string') return user;
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
  return fullName || user.email || user._id || '';
}

export function getUserSiteIds(user) {
  const ids = new Set();
  const primaryId = getId(user?.siteId);
  if (primaryId) ids.add(primaryId);
  (user?.siteIds || []).forEach((site) => {
    const id = getId(site);
    if (id) ids.add(id);
  });
  return [...ids];
}

export function userHasSite(user, siteId) {
  if (!siteId) return false;
  return getUserSiteIds(user).includes(siteId);
}

export function getRoleLabel(role) {
  if (!role) return '';
  if (typeof role === 'string') return role;
  return [role.code, role.label].filter(Boolean).join(' - ') || role._id || '';
}

export function getPermissionLabel(permission) {
  if (!permission) return '';
  if (typeof permission === 'string') return permission;
  return [permission.code, permission.label].filter(Boolean).join(' - ') || permission._id || '';
}
